import dxDataGrid from 'devextreme/ui/data_grid'

import ExcelJS, { Row, Cell } from 'exceljs-hardened'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'

import { readFileAsync } from 'helpers/read-file-async'


const getTemplateRowRef = (tr: Row) => {
    let first: string = ''
    let last: string = ''

    tr.eachCell((cell: Cell) => {
        if (cell.text.match(/^\$\*Spalte[0-9][0-9]/)) {
            if (first === '') {
                first = String.fromCharCode(parseInt(cell.col) + 64)
            }

            last = String.fromCharCode(parseInt(cell.col) + 64)
        }
    })

    return `${first}${tr.number}:${last}${tr.number}`
}

export const excelExporter = async (component: dxDataGrid, name: string, template: File | null, selectedRowsOnly: boolean = false) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('exportSheet')

    const textVars: {[k: string]: string} = {
        '$Text01': name,
        '$Text02': new Date().toISOString()
    }

    const res: any = await exportDataGrid({
        component,
        worksheet,
        selectedRowsOnly: selectedRowsOnly,
    })

    const rows: any[] = []
    worksheet.eachRow((row) => {
        if (row.number >= res.from.row && row.number <= res.to.row) {
            rows.push(row)
        }
    })

    if (template) {
        const templateData: string | ArrayBuffer | null = await readFileAsync(template)

        if (templateData) {
            await workbook.xlsx.load(templateData as ArrayBuffer)
            const templateWs = workbook.getWorksheet(1) as any

            let headerRow: any = null
            let templateRow: any = null

            // find header and data/template rows
            // also look for $Text fields to be replaced
            templateWs.eachRow((row: Row) => {
                row.eachCell((cell: Cell) => {
                    if (!headerRow && cell.text === '$SpalteHdr01') {
                        headerRow = row
                    }

                    if (!templateRow && cell.text === '$*Spalte01') {
                        templateRow = row
                    }

                    if (cell.text.match(/^\$Text[0-9][0-9]$/)) {
                        if (textVars.hasOwnProperty(cell.text)) {
                            cell.value = textVars[cell.text]
                        } else {
                            cell.value = ''
                        }
                    }
                })
            })

            if (!headerRow || !templateRow) {
                alert('missing header or template row')
                throw new Error('missing header or template row')
            }

            let firstCol: string = ''
            let lastCol: string = ''

            headerRow.eachCell((cell: Cell) => {
                if (cell.text.match(/^\$SpalteHdr/)) {
                    const index = parseInt(cell.text.replace('$SpalteHdr', ''))

                    // store beginning of header values
                    if (index === 1) {
                        firstCol = String.fromCharCode(parseInt(cell.col) + 64)
                    }

                    const headerCell = rows[0].getCell(index)
                    cell.value = headerCell.value

                    if (!headerCell.value) {
                        // clear style if remaining cells are empty
                        cell.style = Object.create(cell.style)
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'none',
                            fgColor: { argb: 'FF000000' }
                        }
                        cell.border = {}
                    } else {
                        // store the last column, that has value in it
                        lastCol = String.fromCharCode(parseInt(cell.col) + 64)
                    }
                }
            })

            // fix the header row autofilter option so it does not span outside the filled header cells
            templateWs.autoFilter = {
                from: `${firstCol}${headerRow.number}`,
                to: `${lastCol}${headerRow.number}`
            }

            const templateRowRef = getTemplateRowRef(templateRow)
            const cf = templateWs.conditionalFormattings.find((format: any) => format.ref === templateRowRef)

            rows.slice(1).reverse().forEach((row) => {
                templateWs.duplicateRow(templateRow.number, 1, true)
                const newRow = templateWs.getRow(templateRow.number + 1)
                newRow.eachCell((cell: Cell) => {
                    const index = parseInt(cell.text.replace('$*Spalte', ''))
                    const valueCell = row.getCell(index)
                    cell.value = valueCell.value
                    cell.style = {
                        ...cell.style,
                        numFmt: valueCell.numFmt
                    }
                    cell.alignment = valueCell.alignment
                })
            })

            // add conditional formatting for the new table
            const newRef = `${firstCol}${templateRow.number}:${lastCol}${templateRow.number + rows.length - 2}`
            const newCf = Object.assign({}, cf, {
                ref: newRef
            })
            templateWs.addConditionalFormatting(newCf)
            templateWs.removeConditionalFormatting((formatting: any) => {
                return formatting.ref !== templateRowRef
            })

            // remove template row and leave only the data
            templateWs.spliceRows(templateRow.number, 1)
        }
    }

    // write everything to file
    const buffer = await workbook.xlsx.writeBuffer()
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Datagrid.xlsx')
}
